/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { routes, RoutesType } from '../config';
import { AnyFunction } from 'types';

// We do it like this to be easier to define the type without tapping in
// the complex RoutesType type
type BaseRoutes = Record<string, { getPath?: (params: any) => void; path: string }>;

type Routing<Routes extends BaseRoutes> = {
  [Key in keyof Routes]: Routes[Key]['getPath'] extends AnyFunction
    ? (...params: Parameters<Routes[Key]['getPath']>) => void
    : () => void;
};

export type UseRouting = Routing<RoutesType>;

/**
 *
 * Use this helper hook to get an object that can navigate to any app route
 *
 * Each route will be a function that navigates to it
 *
 * If there are path parameters, those will be expected
 *
 * Query params are defined and optional
 */
export function useRouting(): UseRouting {
  const push = useNavigate();

  return useMemo(
    () =>
      Object.fromEntries(
        Object.entries(routes).map(([key, config]) => [
          key,
          (params: any) =>
            push(
              typeof (config as any).getPath === 'function'
                ? (config as any).getPath?.(params)
                : config.path,
            ),
        ]),
      ),
    [push],
  ) as UseRouting;
}
